import { API_BASE } from "./config";
import { bounceIfUnauthorized } from "./session";

/**
 * Jobs — Setup's maintenance list (`5e`, and `6j`–`6j4` on a phone).
 *
 * Replaces the Pipeline client. The load-bearing idea from the design: **a schedule is a property
 * of a job, not a category of job.** There is no manual list and scheduled list — every job has a
 * trigger, `manual` is one of its modes, and every row has a Run-now button either way.
 *
 * **The name undersells it now.** This module also owns the app's single `EventSource`, and that
 * stream carries `collectionChanged` — which is nothing to do with jobs. Left named `jobs.ts`
 * because the stream is inseparable from the job frames it also carries and renaming would churn
 * every import for a filename; the stream section below is where to look.
 */

export type ScheduleMode = "manual" | "interval" | "daysOfWeek" | "dayOfMonth";

/**
 * `idle` covers never-run *and* scheduled-and-waiting; the difference shows in `lastRun`.
 *
 * `queued` is a property of a job, never of a run — nothing has started, so there is no run record
 * and no verdict. That is why it sits beside `idle` and `running` rather than with the three
 * verdicts after it.
 */
export type JobStateName = "idle" | "queued" | "running" | "passed" | "failed" | "cancelled";

export interface Trigger {
  mode: ScheduleMode;
  everyNDays: number | null;
  /** 0 = Sunday, matching the design's `S M T W T F S` toggles and `Date.getDay()`. */
  days: number[];
  dayOfMonth: number | null;
  time: string;
  /** IANA name. Sent from the browser, because "03:00 local" means nothing on a UTC container. */
  timezone: string;
  notifyOnFailure: boolean;
  /**
   * Where this job sits in line among jobs sharing its time — **lower runs first** (日本-307).
   *
   * Two jobs sharing a time *and* a priority have no defined order, so neither runs on schedule and
   * both come back `blocked`. Manual `Run now` is never blocked by it. Stepped by 10 by convention
   * so there is room to slot a job between two others; nothing enforces the step.
   */
  priority: number;
}

export interface JobRun {
  id: number;
  jobId: string;
  startedAt: number;
  finishedAt: number | null;
  verdict: JobStateName;
  processed: number;
  total: number;
  changed: number;
  durationMs: number;
  /** Its moment passed while the server was down, so it ran at the next start. */
  wasLate: boolean;
  /** Only what a number cannot say — which fields, which file, why it stopped. Often empty. */
  detail: string;
  /**
   * The `This run` card — every number the run produced, labelled, in the server's order (what
   * succeeded before what refused). A key may end in a bracketed unit, `Snapshot size (MB)`;
   * `tallyEntries` splits it off. Values may be fractional.
   */
  tally: Record<string, number>;
  errorCount: number;
  failedNoteIds: number[];
}

export interface Job {
  id: string;
  name: string;
  description: string;
  trigger: Trigger;
  state: JobStateName;
  lastRun: JobRun | null;
  /**
   * Epoch seconds, rendered client-side so `in 4 days` is in the viewer's locale.
   *
   * **Always the honest arithmetic**, even while this row is running, queued or blocked — the server
   * does not null it to force a rendering. What the Next column shows is `nextColumn` in
   * `surfaces/setup/schedule.ts`, which reads `state` and `blocked` first.
   */
  nextRunAt: number | null;
  /** 1-based place in line, set exactly when `state` is `queued`. */
  queuePosition: number | null;
  /**
   * Shares a time AND a priority with another job, so neither runs on schedule. **Run now still
   * works** — this blocks the clock, not the button.
   */
  blocked: boolean;
  /** What Cancel actually does for this job, when that is not obvious. */
  cancelNote: string;
  retryable: boolean;
  /**
   * Numbers this job takes as input — 日本-342. **Empty for every job but one.**
   *
   * The spec travels with the job rather than being known here, so a setting declared in the
   * server's `registry.py` renders in its editor with no change on this side. An empty array draws
   * no Settings card at all, rather than an empty one.
   */
  settings: JobSetting[];
  /**
   * The current value of every key in `settings`, defaults already filled in by the server.
   * Always complete, so nothing here ever needs to know a job's defaults.
   */
  settingValues: Record<string, number>;
}

export interface JobSetting {
  key: string;
  label: string;
  default: number;
  minimum: number;
  maximum: number;
  /** Rendered after the field — `Healed after [90] days without a lapse`. Not part of the value. */
  unit: string;
  /** One sentence under the field, where a threshold says what it actually means. */
  help: string;
}

export interface JobProgress {
  jobId: string;
  phase: string;
  current: number;
  total: number;
  item: string | null;
  message: string | null;
  elapsedSeconds: number;
}

export interface JobStatus {
  jobId: string;
  state: JobStateName;
  runId: number;
  message: string | null;
}

export interface JobsSnapshot {
  jobs: Job[];
  runningJobId: string | null;
  /** The frame in flight when this was read — see the server's note. Without it a page opened
   *  mid-run shows a running job with no numbers until the next card ticks. */
  live: JobProgress | null;
}

export interface JobError {
  noteId: number | null;
  expression: string;
  field: string;
  reason: string;
}

/** The browser's own zone, sent with a schedule so the time survives a UTC server and DST. */
export function localTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

async function json<T>(path: string, init?: RequestInit, what = "jobs"): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { cache: "no-store", ...init });
  if (!res.ok) {
    bounceIfUnauthorized(res);
    // The server writes 400s and 409s for this UI to show verbatim — a schedule that will not
    // save is only actionable if it says which field is wrong.
    const detail = await res
      .json()
      .then((b) => (b as { detail?: string }).detail)
      .catch(() => undefined);
    throw new Error(detail ?? `Could not ${what} (${res.status})`);
  }
  return res.json();
}

export function listJobs(): Promise<JobsSnapshot> {
  return json<JobsSnapshot>("/api/jobs", undefined, "read the job list");
}

export function runJob(id: string): Promise<unknown> {
  return json(`/api/jobs/${id}/run`, { method: "POST" }, "start the job");
}

/** Re-run over the previous run's failures only — offered before Run-all, per the design. */
export function retryJob(id: string): Promise<unknown> {
  return json(`/api/jobs/${id}/retry`, { method: "POST" }, "retry the failed cards");
}

export function cancelJob(id: string): Promise<unknown> {
  return json(`/api/jobs/${id}/cancel`, { method: "POST" }, "cancel the job");
}

export function saveTrigger(id: string, trigger: Trigger): Promise<Job> {
  return json<Job>(
    `/api/jobs/${id}/trigger`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(trigger),
    },
    "save the schedule",
  );
}

/**
 * Save one or more of a job's settings, and take back the whole job.
 *
 * **A partial payload is honoured**, so the editor sends only the field that changed and cannot
 * reset its neighbours. Out-of-range values are a 400 carrying a message written for display —
 * deliberately unlike `saveTrigger`, whose priority is accepted even when it collides.
 */
export function saveJobSettings(id: string, values: Record<string, number>): Promise<Job> {
  return json<Job>(
    `/api/jobs/${id}/settings`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ values }),
    },
    "save the setting",
  );
}

export function jobRuns(id: string): Promise<{ runs: JobRun[] }> {
  return json(`/api/jobs/${id}/runs`, undefined, "read the run history");
}

export function jobRunErrors(id: string, runId: number): Promise<{ errors: JobError[] }> {
  return json(`/api/jobs/${id}/runs/${runId}/errors`, undefined, "read the run's errors");
}

/**
 * "Something wrote to the collection", and who did it.
 *
 * Published by `AnkiCollection.write` itself, so it covers every writer rather than only jobs —
 * MCP `add_note` / `delete_notes`, `/bulk/*` applies, deck and tag admin, an add from Read.
 *
 * `writer` is this tab's `CLIENT_ID` when this tab caused the write, and **null for anything the
 * browser did not do** — the MCP, a Cowork bulk pass, the 03:00 scheduler. A tab ignores its own,
 * because the surface that wrote already updated itself; see `clientId.ts` for why that is a
 * narrower claim than "I sent the request".
 */
export interface CollectionChanged {
  writer: string | null;
}

/**
 * A file the tray agent uploaded has landed in the media store.
 *
 * **Not a violation of `collectionEvents.ts`'s "blunt on purpose" rule**, and it is worth saying so
 * where someone will read it before deleting this. That rule argues against a frame *declaring what
 * it touched* — a `touches` scope claim that can silently understate and reproduce the staleness the
 * whole signal exists to fix. This is the opposite shape: a fact about one specific new object,
 * which the server either wrote or did not. Nothing infers anything from its absence.
 *
 * It exists because a capture's text arrives *before* its screenshot has finished uploading. The
 * alternative was to let the `<img>` 404 and retry — which is both the first polling loop in this
 * app and genuinely expensive: `GET /api/media` runs a media sync-down against the hub before every
 * read, including the ones that miss.
 *
 * `captureId` is the agent's guid, not the filename, because Anki may store the file under a
 * different name than it was given.
 */
export interface MediaStored {
  captureId: string;
  filename: string;
  /** The Anki field value, ready to put straight in a slot. */
  value: string;
  slot: string | null;
}

type JobHandlers = {
  onProgress?: (progress: JobProgress) => void;
  onStatus?: (status: JobStatus) => void;
  /** Fires for EVERY writer, this tab included — filter on `writer` if that matters to you. */
  onCollectionChanged?: (frame: CollectionChanged) => void;
  /** Fires for every upload that carried a capture id, including other tabs' — match on it. */
  onMediaStored?: (frame: MediaStored) => void;
};

/** Reconnect backoff after a stream fails outright. Doubles to the ceiling, resets on a frame. */
const RECONNECT_MIN_MS = 2_000;
const RECONNECT_MAX_MS = 60_000;

/**
 * One `EventSource` for the whole app, shared by everyone who subscribes.
 *
 * It used to be one per caller, and there were already two live whenever Setup was open — Setup's
 * own job list, and the rail's failed-job badge. Every count that now refreshes on a job event
 * would have added another connection carrying the identical frames.
 *
 * **The refcount is a count, not a boolean.** Closing on the first unsubscribe takes the stream out
 * from under everybody else, and the symptom is a surface that silently stops updating rather than
 * anything that errors. In development StrictMode's mount / unmount / remount walks it 1 → 0 → 1 and
 * reopens the stream once; that is acceptable, and a deferred close to smooth it over is a leak
 * waiting to be written.
 *
 * **A shared stream has to be able to die and come back, which a per-caller one did not.** An
 * `EventSource` reconnects on its own only when an *open* stream drops; a non-200 or a wrong
 * content-type — a 401 once the cookie expires, a 502 while the hub restarts — is a permanent
 * failure, and the object stays `CLOSED` forever. That used to self-heal, because leaving Setup and
 * coming back built a new one. It no longer can: `App` subscribes for the life of the tab, so the
 * count never reaches zero and the dead stream would be pinned there — freezing the failed-job
 * badge, Setup's live list, and every count that refreshes on a job event, with nothing on screen
 * saying so. Hence the reopen below.
 */
const subscribers = new Set<JobHandlers>();
let source: EventSource | null = null;
let reopenTimer: number | null = null;
let reopenDelay = RECONNECT_MIN_MS;

function openStream(): EventSource {
  const opened = new EventSource(`${API_BASE}/api/events`);
  const parse = <T,>(event: Event): T | null => {
    try {
      return JSON.parse((event as MessageEvent).data) as T;
    } catch {
      return null;
    }
  };
  // A frame arriving proves the stream is healthy, so the backoff starts from scratch next time.
  opened.addEventListener("open", () => {
    reopenDelay = RECONNECT_MIN_MS;
  });
  opened.addEventListener("jobProgress", (e) => {
    const data = parse<JobProgress>(e);
    // Snapshotted, because a handler is free to unsubscribe from inside itself.
    if (data) for (const s of [...subscribers]) s.onProgress?.(data);
  });
  opened.addEventListener("jobStatus", (e) => {
    const data = parse<JobStatus>(e);
    if (data) for (const s of [...subscribers]) s.onStatus?.(data);
  });
  opened.addEventListener("collectionChanged", (e) => {
    const data = parse<CollectionChanged>(e);
    if (data) for (const s of [...subscribers]) s.onCollectionChanged?.(data);
  });
  // Spelled exactly as `anki_mcp/media.py`'s MEDIA_STORED. A frame whose name nobody registered is
  // dropped in silence — see the note on `openStream`'s contract in jobs.test.ts.
  opened.addEventListener("mediaStored", (e) => {
    const data = parse<MediaStored>(e);
    if (data) for (const s of [...subscribers]) s.onMediaStored?.(data);
  });
  opened.addEventListener("error", () => {
    // Only a CLOSED stream is ours to replace. `error` also fires on the transient drops the
    // browser retries by itself, and racing it would open a second connection carrying the same
    // frames — which is the thing this module exists to stop.
    if (opened.readyState !== EventSource.CLOSED) return;
    if (source === opened) source = null;
    scheduleReopen();
  });
  return opened;
}

/**
 * Come back after a permanent failure, backing off so a hub that is down does not turn into a
 * reconnect loop. Only while somebody is still listening — nothing reopens a stream nobody wants.
 */
function scheduleReopen(): void {
  if (reopenTimer !== null || subscribers.size === 0) return;
  const delay = reopenDelay;
  reopenDelay = Math.min(reopenDelay * 2, RECONNECT_MAX_MS);
  reopenTimer = window.setTimeout(() => {
    reopenTimer = null;
    if (subscribers.size > 0 && !source) source = openStream();
  }, delay);
}

/** Subscribe to job events. Returns an unsubscribe function. */
export function subscribeToJobs(handlers: JobHandlers): () => void {
  subscribers.add(handlers);
  if (!source) source = openStream();
  return () => {
    subscribers.delete(handlers);
    if (subscribers.size > 0) return;
    if (source) {
      source.close();
      source = null;
    }
    if (reopenTimer !== null) {
      window.clearTimeout(reopenTimer);
      reopenTimer = null;
    }
    reopenDelay = RECONNECT_MIN_MS;
  };
}
